/**
 * "Billing" tab of the runtime System administration console — a consuming
 * ORGANIZATION's side of resource billing: each app it subscribes to, this
 * month's attributed usage and charge from the accepted price sheet, and the
 * organization money wallet the monthly settlement debits.
 *
 * Scoped server-side to the caller's own tenant (`/api/org/billing/*`).
 */
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Loader2, Wallet } from 'lucide-react'
import {
  acceptGrantSheet, formatMoney, formatUsage, getGrantPriceSheet,
  getOrgInvoices, getOrgSubscriptions, invoiceDocumentUrl, topupOrgWallet,
  type OrgInvoice, type OrgSubscriptions, type PriceSheet,
} from '@/api/billing'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'

const card: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 12, padding: '1rem 1.25rem', marginBottom: 16,
}
const hint: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '.04em',
  textTransform: 'uppercase', color: 'var(--color-text-secondary)',
  padding: '6px 8px', borderBottom: '1px solid var(--color-border-strong)',
}
const td: React.CSSProperties = { padding: '8px', borderBottom: '1px solid var(--color-border)' }

interface SheetView {
  grantId: number
  project: string
  provider: string
  accepted: PriceSheet | null
  current: PriceSheet | null
  newer: boolean
}

export function OrgBillingPanel() {
  const { checkout, busy } = useRazorpayCheckout()
  const [data, setData] = useState<OrgSubscriptions | null>(null)
  const [invoices, setInvoices] = useState<OrgInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [topUpFor, setTopUpFor] = useState<string | null>(null)   // currency
  const [amount, setAmount] = useState('')
  const [sheetView, setSheetView] = useState<SheetView | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await getOrgSubscriptions())
      try {
        setInvoices((await getOrgInvoices()).invoices)
      } catch {
        setInvoices([])
      }
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const addFunds = async () => {
    const major = parseFloat(amount)
    if (!(major > 0) || !topUpFor) { toast.error('Enter an amount'); return }
    const result = await checkout({
      purpose: 'org_wallet_topup',
      description: 'Organization wallet top-up',
      precreate: () => topupOrgWallet(Math.round(major * 100), topUpFor),
    })
    if (result.cancelled) return
    if (!result.ok) { toast.error(result.error || 'The payment did not complete'); return }
    toast.success('Funds added')
    setTopUpFor(null)
    setAmount('')
    void load()
  }

  const openSheet = async (grantId: number, project: string, provider: string) => {
    try {
      const info = await getGrantPriceSheet(grantId)
      setSheetView({
        grantId, project, provider,
        accepted: info.accepted, current: info.current,
        newer: Boolean(info.newer_available),
      })
    } catch {
      toast.error('Could not load the price sheet')
    }
  }

  const acceptNewer = async () => {
    if (!sheetView) return
    try {
      await acceptGrantSheet(sheetView.grantId)
      toast.success('Price sheet accepted')
      setSheetView(null)
      void load()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not accept the price sheet')
    }
  }

  if (loading) return <Loader2 size={18} className="spin" />
  if (!data?.enabled) {
    return <p style={hint}>Usage-based billing is not enabled on this platform.</p>
  }

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Apps your organization subscribes to</div>
        <div style={{ ...hint, margin: '2px 0 10px' }}>
          Usage-based, measured monthly against each app&apos;s accepted price sheet.
          Charged to the organization wallet on the 1st.
        </div>
        {data.subscriptions.length === 0 ? (
          <p style={hint}>No subscriptions — apps granted to your organization appear here.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={th}>App</th>
                  <th style={th}>Provider</th>
                  <th style={th}>This month&apos;s usage</th>
                  <th style={{ ...th, textAlign: 'right' }}>Month-to-date</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.map((s) => (
                  <tr key={s.grant_id}>
                    <td style={{ ...td, fontWeight: 500 }}>{s.project_id}</td>
                    <td style={td}>{s.provider}</td>
                    <td style={{ ...td, ...mono, color: 'var(--color-text-secondary)' }}>{formatUsage(s.usage)}</td>
                    <td style={{ ...td, ...mono, textAlign: 'right' }}>{s.month_to_date_display}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        type="button" className="btn btn-ghost btn-sm"
                        onClick={() => void openSheet(s.grant_id, s.project_id, s.provider)}
                      >
                        Price sheet{s.sheet_version ? ` v${s.sheet_version}` : ''}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.wallets.map((w) => (
        <div key={w.currency} style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Wallet size={18} style={{ color: 'var(--color-accent)' }} />
            <div>
              <div style={hint}>Organization wallet ({w.currency})</div>
              <div style={{ ...mono, fontSize: '1.1125rem', fontWeight: 600 }}>{w.display}</div>
            </div>
            <span style={{ ...hint, marginLeft: 12 }}>
              next charges ≈ {w.upcoming_display}
            </span>
            {!w.sufficient && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.8125rem', color: 'var(--color-warning)',
                background: 'var(--color-warning-weak)', borderRadius: 99, padding: '3px 10px',
              }}>
                <AlertTriangle size={12} /> Add funds before the 1st or access pauses after the grace period
              </span>
            )}
          </div>
          <Button variant="primary" size="sm" onClick={() => { setTopUpFor(w.currency || 'INR'); setAmount('') }}>
            Add funds
          </Button>
        </div>
      ))}
      {data.wallets.length === 0 && data.subscriptions.length > 0 && (
        <p style={hint}>A wallet is created with your first paid subscription month.</p>
      )}

      {invoices.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>Monthly statements</div>
          <div style={{ ...hint, margin: '2px 0 10px' }}>
            One usage invoice per app per settled month, issued on the provider&apos;s
            behalf. Line items carry the metered detail; taxes were applied when the
            wallet was funded.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem', minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={th}>Invoice</th>
                  <th style={th}>Issued</th>
                  <th style={th}>App / period</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_number}>
                    <td style={{ ...td, ...mono }}>{inv.invoice_number}</td>
                    <td style={{ ...td, ...mono, color: 'var(--color-text-secondary)' }}>
                      {inv.issued_at ? inv.issued_at.slice(0, 10) : '—'}
                    </td>
                    <td style={{ ...td, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {(inv.line_items?.[0]?.description as string) || '—'}
                    </td>
                    <td style={{ ...td, ...mono, textAlign: 'right', fontWeight: 600 }}>
                      {inv.display?.total ?? formatMoney(inv.total_minor, inv.currency)}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {inv.can_open_document ? (
                        <a
                          href={invoiceDocumentUrl(inv.invoice_number)}
                          target="_blank" rel="noreferrer"
                          className="btn btn-ghost btn-sm"
                        >
                          Open
                        </a>
                      ) : (
                        <span style={hint}>admin only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={topUpFor !== null}
        onClose={() => setTopUpFor(null)}
        title={`Add funds (${topUpFor ?? ''})`}
        footer={(
          <>
            <Button variant="secondary" size="sm" onClick={() => setTopUpFor(null)}>Cancel</Button>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void addFunds()}>Pay</Button>
          </>
        )}
      >
        <p style={{ fontSize: '0.875rem', margin: '0 0 8px' }}>
          Amount to add to your organization wallet:
        </p>
        <input
          type="number" min={1} step="1" autoFocus
          value={amount} onChange={(e) => setAmount(e.target.value)}
          style={{ width: '100%' }}
          placeholder={topUpFor === 'INR' ? 'e.g. 5000' : 'e.g. 50'}
        />
      </Modal>

      <Modal
        open={sheetView !== null}
        onClose={() => setSheetView(null)}
        title={sheetView ? `${sheetView.project} — price sheet` : ''}
        footer={sheetView?.newer ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSheetView(null)}>Later</Button>
            <Button variant="primary" size="sm" onClick={() => void acceptNewer()}>
              Accept new price sheet
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setSheetView(null)}>Close</Button>
        )}
      >
        {sheetView && (
          <div style={{ fontSize: '0.875rem' }}>
            <div style={{ ...hint, marginBottom: 8 }}>Provided by {sheetView.provider}</div>
            {sheetView.newer && (
              <div style={{
                background: 'var(--color-warning-weak)', border: '1px solid var(--color-warning)',
                borderRadius: 8, padding: '8px 10px', marginBottom: 10, fontSize: '0.8125rem',
              }}>
                The provider published a newer price sheet
                {sheetView.current ? ` (v${sheetView.current.version})` : ''}. It applies
                after the notice period; you can accept it now.
              </div>
            )}
            <SheetTable sheet={sheetView.accepted ?? sheetView.current} />
          </div>
        )}
      </Modal>
    </div>
  )
}

function SheetTable({ sheet }: { sheet: PriceSheet | null }) {
  if (!sheet || sheet.mode === 'free') {
    return <p style={hint}>This app is free for your organization.</p>
  }
  if (sheet.mode === 'per_user') {
    return (
      <p style={{ margin: 0 }}>
        <span style={mono}>{formatMoney(sheet.per_user_minor ?? 0, sheet.currency)}</span>
        {' '}per user per month (sheet v{sheet.version}).
      </p>
    )
  }
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8425rem' }}>
      <thead>
        <tr>
          <th style={th}>Meter</th>
          <th style={{ ...th, textAlign: 'right' }}>Free / month</th>
          <th style={{ ...th, textAlign: 'right' }}>Then</th>
        </tr>
      </thead>
      <tbody>
        {Object.values(sheet.meters).map((m) => (
          <tr key={m.meter}>
            <td style={td}>{m.label}</td>
            <td style={{ ...td, ...mono, textAlign: 'right' }}>{m.allowance_qty || '—'}</td>
            <td style={{ ...td, ...mono, textAlign: 'right' }}>
              {formatMoney(m.rate, sheet.currency)} / {m.unit_block === 1 ? '' : `${m.unit_block} `}{m.meter === 'db_gb' || m.meter === 'file_gb' ? 'GB / mo' : m.meter}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
